import { IconArrowLeft, IconX } from "@tabler/icons-react";
import { useEffect, useMemo, type ReactElement } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../Button";
import { IconControlButton } from "../rail/IconControlButton";
import { ThemeToggleButton } from "../ThemeToggleButton";
import { VIEWER_MOBILE_MEDIA_QUERY } from "../viewerBreakpoint";
import { useMatchMedia } from "../useMatchMedia";
import { abbreviatedStreetLine } from "./abbreviateStreetLine";
import { CameraCompassMenu } from "./CameraCompassMenu";
import { ViewerAddressEditor } from "./ViewerAddressEditor";
import type { AddressEditorSkin } from "./addressEditorSkin";
import { ViewerExportMenu } from "./ViewerExportMenu";
import styles from "./ViewerTopNav.module.css";

/**
 * DOM id on the Export menu trigger. Used by catalogue previews and kept
 * stable alongside {@link publishViewerTopNavExportHandler} screenshot wiring.
 */
export const VIEWER_TOP_NAV_EXPORT_HEADER_ID = "export-view-btn-header";

export interface ViewerTopNavModel {
  address: string;
  demLabel: string | null;
  demTitle: string;
  reportExpiryLine: string | null;
  /** Tooltip for report expiry (purchase + expiration dates). */
  reportExpiryTitle: string;
  showRenew: boolean;
  renewHref: string;
  /**
   * Buildability score summary for the top-nav chip (toggles the Report
   * drawer). Null until analysis settles — the chip renders a neutral
   * "Report" affordance in the meantime.
   */
  buildabilityScore: { total: number; letter: string } | null;
}

const navPlaceholderBase: Omit<ViewerTopNavModel, "address" | "renewHref"> = {
  demLabel: null,
  demTitle: "",
  reportExpiryLine: null,
  reportExpiryTitle: "",
  showRenew: false,
  buildabilityScore: null
};

const topAddressSkin: AddressEditorSkin = {
  addressBlock: styles.addressBlock,
  addressPill: styles.addressPill,
  addressPillScanning: styles.addressPillScanning,
  addressReadonly: styles.addressReadonly,
  addressInput: styles.addressInput,
  scanningLabel: styles.scanningLabel,
  clearBtn: styles.clearBtn,
  dropdownPanel: styles.dropdownPanel,
  dropdownStatus: styles.dropdownStatus,
  dropdownStatusError: styles.dropdownStatusError,
  menuItem: styles.menuItem,
  menuItemActive: styles.menuItemActive
};

/**
 * Synchronous headline for `/viewer` while `bootViewer` awaits `bootSite()` and
 * the bridge has not pushed a live model yet. Mirrors the URL precedence order
 * in `bootSite()` (report → address → lat/lon → generic).
 */
export function viewerTopNavPlaceholderModel(search: Pick<URLSearchParams, "get">): ViewerTopNavModel {
  if (search.get("report")?.trim()) {
    return {
      ...navPlaceholderBase,
      address: "Opening report…",
      renewHref: "/"
    };
  }
  const addr = search.get("address")?.trim();
  if (addr) {
    return {
      ...navPlaceholderBase,
      address: addr,
      renewHref: `/?address=${encodeURIComponent(addr)}`
    };
  }
  const latRaw = search.get("lat");
  const lonRaw = search.get("lon");
  const lat = latRaw != null ? Number(latRaw) : NaN;
  const lon = lonRaw != null ? Number(lonRaw) : NaN;
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    const label = `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
    return {
      ...navPlaceholderBase,
      address: label,
      renewHref: `/?lat=${encodeURIComponent(latRaw ?? "")}&lon=${encodeURIComponent(lonRaw ?? "")}`
    };
  }
  return {
    ...navPlaceholderBase,
    address: "Loading terrain…",
    renewHref: "/"
  };
}

export type ViewerTopNavPlacement = "default" | "modalSticky";

export interface ViewerTopNavProps {
  model: ViewerTopNavModel;
  exportButtonId: string;
  onExportScreenshot: () => void;
  /** Reserved for PDF export; optional until implemented. */
  onExportPdf?: () => void;
  /**
   * Sticky viewer bar shell inside scrolling parents (`AvailabilityModal` sheet).
   * Leading control matches legacy `.modal-close` icons; hides compass, export,
   * renew, and address search pill.
   */
  placement?: ViewerTopNavPlacement;
  onModalBack?: () => void;
  /** `aria-label` / `title` for the modal leading control. */
  modalBackAriaLabel?: string;
  /**
   * Iconography for `placement="modalSticky"`: circled ✕ (close) vs circled back
   * chevron (payment step) — matches legacy `.modal-close` / `.modal-back`.
   */
  modalLeadingIcon?: "close" | "back";
  /** When multiple headers render (catalogue previews), avoid duplicate `#viewer-address-input` ids. */
  desktopAddressSearchInputId?: string;
  /**
   * Create Illustration camera framing — same fixed 80px shell and control
   * styles as the default bar; only the bar fill is transparent so the canvas
   * shows through.
   */
  illustrationCaptureActive?: boolean;
  onIllustrationCancel?: () => void;
  onIllustrationContinue?: () => void;
  /** Handheld export sheet open state, lifted to `RootLayout`. */
  mobileExportMenuOpen?: boolean;
  onMobileExportMenuOpenChange?: (open: boolean) => void;
}

export function ViewerTopNav({
  model,
  exportButtonId,
  onExportScreenshot,
  onExportPdf,
  placement = "default",
  onModalBack,
  modalBackAriaLabel,
  modalLeadingIcon = "close",
  desktopAddressSearchInputId = "viewer-address-input",
  illustrationCaptureActive = false,
  onIllustrationCancel,
  onIllustrationContinue,
  mobileExportMenuOpen,
  onMobileExportMenuOpenChange
}: ViewerTopNavProps): ReactElement {
  const isMobile = useMatchMedia(VIEWER_MOBILE_MEDIA_QUERY);
  const modalSticky = placement === "modalSticky";
  const navigate = useNavigate();

  // Default-bar back: return to wherever the user came from (the dashboard, a
  // prior viewer, marketing search…) by stepping back one history entry. Only
  // fall back to the marketing home when there's no in-app history to return
  // to — e.g. a report opened from a direct/email link in a fresh tab.
  const onBack = (): void => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const ariaBack = modalBackAriaLabel ?? "Close";

  useEffect(() => {
    if (!illustrationCaptureActive) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onIllustrationCancel?.();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [illustrationCaptureActive, onIllustrationCancel]);

  const streetLabel = useMemo(
    () => abbreviatedStreetLine(model.address),
    [model.address]
  );

  const navClass = [
    "nav",
    "viewer-top-nav",
    styles.viewerNav,
    modalSticky ? `viewer-top-nav-modal ${styles.viewerNavModalSticky}` : "",
    illustrationCaptureActive ? styles.viewerNavIllustrationCapture : ""
  ]
    .filter(Boolean)
    .join(" ");

  // Modal-sticky keeps the 48px marketing Button so it lines up with the
  // checkout card; the floating viewer islands use the 40px rail control.
  const leadingControl = modalSticky ? (
    <Button
      id="av-close"
      variant="secondary"
      icon={modalLeadingIcon === "back" ? IconArrowLeft : IconX}
      onClick={onModalBack}
      aria-label={ariaBack}
      title={ariaBack}
    />
  ) : illustrationCaptureActive ? (
    <IconControlButton
      icon={IconX}
      onClick={onIllustrationCancel}
      aria-label="Cancel illustration"
      title="Cancel"
    />
  ) : (
    <IconControlButton icon={IconArrowLeft} onClick={onBack} aria-label="Back" />
  );

  const islandClass = modalSticky ? styles.cluster : `viewer-island ${styles.island}`;

  return (
    <nav className={navClass}>
      <div className={`container nav-inner ${styles.navInner}`}>
        <div className={`viewer-top-chrome-slide ${styles.chromeSlideLayer}`}>
          <div className={`viewer-nav-leading ${styles.leadingCluster} ${islandClass}`}>
            {leadingControl}
            {!modalSticky && !illustrationCaptureActive ? (
              <ViewerAddressEditor
                model={model}
                readonlyLabel={isMobile ? streetLabel : model.address}
                readonlyTooltip={isMobile ? model.address : undefined}
                skin={topAddressSkin}
                addressInputId={desktopAddressSearchInputId}
              />
            ) : null}
          </div>

          {modalSticky ? null : illustrationCaptureActive ? (
            <div className={`nav-actions ${styles.trailingCluster} ${islandClass}`}>
              <Button variant="primary" onClick={onIllustrationContinue}>
                Continue
              </Button>
            </div>
          ) : (
            <div className={`nav-actions ${styles.trailingCluster} ${islandClass}`}>
              {!isMobile && model.reportExpiryLine?.trim() ? (
                <div
                  className={styles.reportSkuBar}
                  title={
                    model.reportExpiryTitle.trim()
                      ? model.reportExpiryTitle
                      : undefined
                  }
                >
                  {model.reportExpiryLine}
                </div>
              ) : null}
              {!isMobile ? <ThemeToggleButton compact /> : null}
              <ViewerExportMenu
                exportButtonId={exportButtonId}
                isMobile={isMobile}
                onExportScreenshot={onExportScreenshot}
                onExportPdf={onExportPdf}
                mobileMenuOpen={mobileExportMenuOpen}
                onMobileMenuOpenChange={onMobileExportMenuOpenChange}
              />
              {model.showRenew ? (
                <Button
                  as={Link}
                  to={model.renewHref}
                  variant="primary"
                  className={styles.renewButton}
                >
                  Renew
                </Button>
              ) : null}
            </div>
          )}
        </div>

        {modalSticky ? null : illustrationCaptureActive ? (
          <div className={`viewer-nav-center ${styles.centerCompass}`} role="status">
            <p className={`viewer-island ${styles.illustrationCenterHint}`}>
              Position your camera and zoom for the illustration view.
            </p>
          </div>
        ) : (
          <div className={`viewer-nav-center ${styles.centerCompass}`}>
            <div className={styles.compassIsland}>
              <CameraCompassMenu />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
